import { useCallback, useEffect, useRef, useState } from 'react';
import { BlockStack, Box, Button, InlineStack, Text } from '@shopify/polaris';

/*
  Looks up the PDF drawing and STEP model of the part on Dropbox (file name = product name or reference) and attaches
  them to the product documents (POST /api/products/:id/dropbox-files). autoRun starts the lookup once without a
  click, used right after a new product was saved with "Search Dropbox" checked.
*/
function describeResult(result) {
  const attached = result.attached || [];
  const skipped = result.skipped || [];
  const failed = result.failed || [];
  const lines = [];
  if (attached.length) lines.push({ tone: 'success', text: `Attached from Dropbox: ${attached.map((file) => file.filename).join(', ')}` });
  if (skipped.length) lines.push({ tone: 'subdued', text: `Already attached: ${skipped.map((file) => file.filename).join(', ')}` });
  failed.forEach((file) => lines.push({ tone: 'critical', text: `${file.filename || 'Lookup'} failed: ${file.reason}` }));
  if (!lines.length) {
    const names = (result.searchedNames || []).join(' / ');
    lines.push({ tone: 'subdued', text: names ? `No PDF or STEP file named ${names} found on Dropbox.` : 'The product has no name or reference to search for.' });
  }
  return lines;
}

export default function DropboxPartFilesLookup({ productId, autoRun = false, onAttached = () => {} }) {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([]);
  const autoRanFor = useRef(null);

  const runLookup = useCallback(async () => {
    setLoading(true);
    setLines([]);
    try {
      const response = await fetch(`/api/products/${productId}/dropbox-files`, { method: 'POST' });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.msg || 'The Dropbox lookup failed.');
      setLines(describeResult(data));
      if (data.attached?.length) onAttached(data);
    } catch (error) {
      setLines([{ tone: 'critical', text: error.message || 'The Dropbox lookup failed.' }]);
    } finally {
      setLoading(false);
    }
  }, [productId, onAttached]);

  useEffect(() => {
    setLines([]);
    if (autoRun && productId && autoRanFor.current !== productId) {
      autoRanFor.current = productId;
      runLookup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, autoRun]);

  return (
    <Box paddingBlockEnd="200">
      <BlockStack gap="100">
        <InlineStack gap="200" blockAlign="center">
          <Button size="slim" onClick={runLookup} loading={loading}>
            Search Dropbox for PDF & STEP
          </Button>
          {loading ? (
            <Text as="span" tone="subdued" variant="bodySm">
              Searching Dropbox…
            </Text>
          ) : null}
        </InlineStack>
        {lines.map((line) => (
          <Text key={line.text} as="p" variant="bodySm" tone={line.tone}>
            {line.text}
          </Text>
        ))}
      </BlockStack>
    </Box>
  );
}
